import React, { useMemo, useState } from "react";
import { Box, ButtonBase, InputBase, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CATERING_COLOR, CATERING_SOFT } from "../../utils/catering";

// The Catering page's event pickers, after ezCater's: a month calendar beside
// the arrival times, and a guest count. Controlled — the page keeps a draft
// and applies it with "Update results".

const INK = "#1D1A18";
const MUTED = "#5F5955";
const LINE = "#ECE6E0";
const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const fmtTime = (value) => new Date(`2000-01-01T${value}:00`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

// Arrival times, every half hour from 7:00 AM to 9:30 PM.
export const TIMES = Array.from({ length: 30 }, (_, i) => {
  const minutes = 7 * 60 + i * 30;
  const value = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
  return { value, label: fmtTime(value) };
});

const label = { fontSize: "1rem", fontWeight: 800, color: INK, mb: 1.5 };

/** Month calendar; days before `min` are off. */
function MonthCalendar({ value, min, onPick }) {
  const start = value ? new Date(`${value}T00:00:00`) : new Date(`${min}T00:00:00`);
  const [month, setMonth] = useState(new Date(start.getFullYear(), start.getMonth(), 1));
  const minMonth = new Date(`${min}T00:00:00`);
  const atFirst = month.getFullYear() === minMonth.getFullYear() && month.getMonth() === minMonth.getMonth();
  const cells = useMemo(() => {
    const lead = month.getDay();
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
    ];
  }, [month]);
  const step = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const arrow = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    color: INK,
    "&:hover": { bgcolor: "#F7F3EF" },
    "&.Mui-disabled": { color: "#C9C2BC" },
    "&:focus-visible": { outline: `3px solid ${INK}`, outlineOffset: 1 },
  };
  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontWeight: 700, color: INK, pl: 1 }} aria-live="polite">
          {month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </Typography>
        <Box>
          <ButtonBase sx={arrow} onClick={() => step(-1)} disabled={atFirst} aria-label="Previous month">
            <ArrowBackRoundedIcon fontSize="small" />
          </ButtonBase>
          <ButtonBase sx={arrow} onClick={() => step(1)} aria-label="Next month">
            <ArrowForwardRoundedIcon fontSize="small" />
          </ButtonBase>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: 0.5 }} role="grid">
        {WEEKDAYS.map((d) => (
          <Typography key={d} sx={{ textAlign: "center", fontSize: "0.8rem", fontWeight: 600, color: MUTED, py: 0.5 }}>
            {d}
          </Typography>
        ))}
        {cells.map((day, i) => {
          if (!day) return <Box key={`blank-${i}`} />;
          const key = ymd(day);
          const off = key < min;
          const on = key === value;
          return (
            <ButtonBase
              key={key}
              disabled={off}
              onClick={() => onPick(key)}
              aria-label={day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              aria-pressed={on}
              sx={{
                justifySelf: "center",
                width: 40,
                height: 40,
                borderRadius: "12px",
                fontSize: "0.95rem",
                fontWeight: on ? 800 : 600,
                color: off ? "#C9C2BC" : on ? "#fff" : INK,
                bgcolor: on ? CATERING_COLOR : "transparent",
                "&:hover": { bgcolor: on ? CATERING_COLOR : "#F7F3EF" },
                "&:focus-visible": { outline: `3px solid ${INK}`, outlineOffset: 1 },
              }}
            >
              {day.getDate()}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * Date and arrival time. Times already past today are off.
 */
export function EventWhen({ date, time, onDate, onTime, stacked }) {
  const today = ymd(new Date());
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: stacked ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 3,
        }}
      >
        <Box>
          <Typography sx={label}>Event date</Typography>
          <MonthCalendar value={date} min={today} onPick={onDate} />
        </Box>
        <Box>
          <Typography sx={label}>Arrival time</Typography>
          <Box
            role="listbox"
            aria-label="Arrival time"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
              maxHeight: stacked ? 196 : 292,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {TIMES.map((t) => {
              const [h, m] = t.value.split(":").map(Number);
              const past = date === today && h * 60 + m <= nowMinutes;
              const on = t.value === time;
              return (
                <ButtonBase
                  key={t.value}
                  role="option"
                  aria-selected={on}
                  disabled={past}
                  onClick={() => onTime(t.value)}
                  sx={{
                    height: 44,
                    borderRadius: 999,
                    border: `1px solid ${on ? CATERING_COLOR : LINE}`,
                    bgcolor: on ? CATERING_COLOR : "#fff",
                    color: past ? "#C9C2BC" : on ? "#fff" : INK,
                    fontSize: "0.88rem",
                    fontWeight: on ? 800 : 600,
                    "&:hover": { borderColor: on ? CATERING_COLOR : INK },
                    "&:focus-visible": { outline: `3px solid ${INK}`, outlineOffset: 1 },
                  }}
                >
                  {t.label}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          mt: 2.5,
          p: "12px 14px",
          borderRadius: "12px",
          bgcolor: CATERING_SOFT,
          color: INK,
          fontSize: "0.92rem",
          fontWeight: 600,
        }}
      >
        <InfoOutlinedIcon sx={{ color: CATERING_COLOR, fontSize: 22 }} />
        Your event date shows only the caterers who can make it.
      </Box>
    </Box>
  );
}

/** Number of guests: − [n] +, typed or stepped. */
export function EventGuests({ guests, onGuests, min = 1, max = 500 }) {
  const n = guests ?? "";
  const set = (v) => onGuests(v === "" ? null : Math.min(max, Math.max(min, v)));
  const btn = {
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: `1.5px solid ${INK}`,
    color: INK,
    "&.Mui-disabled": { borderColor: "#C9C2BC", color: "#C9C2BC" },
    "&:focus-visible": { outline: `3px solid ${INK}`, outlineOffset: 2 },
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", columnGap: 2, rowGap: 1.5 }}>
      <Typography id="event-guests-label" sx={{ fontSize: "1rem", fontWeight: 800, color: INK }}>
        Number of guests
      </Typography>
      <Box role="group" aria-labelledby="event-guests-label" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ButtonBase sx={btn} onClick={() => set((guests || min + 1) - 1)} disabled={!guests || guests <= min} aria-label="Fewer guests">
          <RemoveRoundedIcon />
        </ButtonBase>
        <InputBase
          value={n}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^0-9]/g, "");
            set(digits === "" ? "" : Number(digits));
          }}
          inputProps={{ inputMode: "numeric", "aria-label": "Number of guests", style: { textAlign: "center" } }}
          placeholder="—"
          sx={{
            width: 72,
            height: 44,
            px: 1,
            borderRadius: "12px",
            border: `1px solid ${LINE}`,
            fontSize: "1.05rem",
            fontWeight: 800,
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        />
        <ButtonBase sx={btn} onClick={() => set((guests || 0) + 1)} disabled={guests >= max} aria-label="More guests">
          <AddRoundedIcon />
        </ButtonBase>
      </Box>
    </Box>
  );
}
